import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { Field, getAnnotationFieldName } from '@aiwyn/shared';
import Annotation = Core.Annotations.Annotation;

export enum DocumentFieldType {
  SIGNATURE = 'Signature',
  INITIAL = 'Initial',
  DATE = 'Date',
  // NAME = 'Name',
  // TITLE = 'Title',
  NAME_AND_TITLE = 'NameAndTitle',
  CONSENT = 'Consent',
}

export enum AnnotationType {
  SIGNATURE = 'SIGNATURE',
  TEXT = 'TEXT',
  RADIO = 'RADIO',
}

export type NavigableAnnotation = SignatureAnnotation | TextAnnotation | RadioAnnotation;

export interface BaseNavigableAnnotation {
  type: AnnotationType;
  index: number;
  data: Annotation;
}

export interface SignatureAnnotation extends BaseNavigableAnnotation {
  type: AnnotationType.SIGNATURE;
  isSigned: boolean;
}

export interface TextAnnotation extends BaseNavigableAnnotation {
  type: AnnotationType.TEXT;
  populated: boolean;
}

export interface RadioAnnotation extends BaseNavigableAnnotation {
  type: AnnotationType.RADIO;
  linkedTo: Annotation[];
}

/**
 * Helper interface for field name parsing functions
 */
export interface FieldNameHelpers {
  getFieldType: (name: string) => DocumentFieldType;
  getFieldDocumentContactId: (name: string) => number;
  getSignatureBlockNumber: (name: string) => number;
}

/**
 * Extracts the field type from a field name
 * @param fieldName The field name to parse
 * @returns The document field type
 */
export function getFieldType(fieldName: string): DocumentFieldType {
  return fieldName?.split('.')[3]?.replace(/_\d*/, '') as DocumentFieldType;
}

/**
 * Gets the document contact ID from a field name
 * @param fieldName The field name to parse
 * @returns The document contact ID
 */
export function getFieldDocumentContactId(fieldName: string): number {
  return +fieldName?.split('.')[2];
}

/**
 * Gets the signature block number from a field name
 * @param fieldName The field name to parse
 * @returns The signature block number
 */
export function getSignatureBlockNumber(fieldName: string): number {
  return +fieldName.substring(fieldName.indexOf('_') + 1);
}

/**
 * Gets the field type from an annotation
 * @param annotation The annotation to get the field type from
 * @returns The document field type
 */
export function getAnnotationFieldType(annotation: Annotation): DocumentFieldType {
  return getFieldType(getAnnotationFieldName(annotation));
}

/**
 * Gets the document contact ID from an annotation
 * @param annotation The annotation to get the document contact ID from
 * @returns The document contact ID
 */
export function getAnnotationDocumentContactId(annotation: Annotation): number {
  return +getAnnotationFieldName(annotation)?.split('.')[2];
}

/**
 * Gets the signature block number from an annotation
 * @param annotation The annotation to get the signature block number from
 * @returns The signature block number
 */
export function getAnnotationSignatureBlockNumber(annotation: Annotation): number {
  return getSignatureBlockNumber(getAnnotationFieldName(annotation));
}

/**
 * Finds a field by name
 * @param fields The fields to search
 * @param fieldName The field name to find
 * @returns The found field or undefined
 */
export function findFieldByName(fields: Field[], fieldName: string): Field {
  return fields.find(({ name }) => name === fieldName);
}

export interface SignButtonParams {
  actionLabel: string;
  contactName: string;
  contactEmail: string;
  hasContactInfo: boolean;
  disabled?: boolean;
}

export interface InitialsButtonParams {
  actionLabel: string;
  initials: string;
  disabled?: boolean;
}

/**
 * Escapes a string for safe interpolation into an HTML template assigned via innerHTML.
 * Sign-here element content is built with template literals and bypasses Angular's
 * sanitization pipeline, so any user-controlled value (contact name, email, initials)
 * MUST be passed through this helper to prevent stored XSS.
 */
export function escapeHtml(value: string): string {
  if (value == null) {
    return '';
  }

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Builds the "who to contact" fragment for the multi-identity signing dialog, degrading from a
 * mailto: link to a bare name to generic copy. The backend omits managerEmail when the manager
 * has no address on file or is inactive, so every tier here is a real case.
 *
 * Returns escaped HTML for innerHTML interpolation — see escapeHtml.
 */
export function getContactManagerHTML(managerName?: string, managerEmail?: string): string {
  const name = managerName?.trim();
  const email = managerEmail?.trim();

  if (email) {
    const safeEmail = escapeHtml(email);
    const label = name ? escapeHtml(name) : safeEmail;
    return `please contact <a href="mailto:${safeEmail}" target="_blank"><strong>${label}</strong></a>`;
  }

  if (name) {
    return `please contact your engagement manager, <strong>${escapeHtml(name)}</strong>`;
  }

  return 'please contact your firm';
}

export function getSignButtonHTML({ actionLabel, contactName, contactEmail, hasContactInfo, disabled = false }: SignButtonParams): string {
  const contactInfoHTML = hasContactInfo
    ? `
      <div class="sign-button-info">
        <span class="sign-button-name">${escapeHtml(contactName)}</span>
        <br />
        ${contactEmail ? `<span class="sign-button-email">${escapeHtml(contactEmail)}</span>` : ''}
      </div>
    `
    : '';

  const disabledClass = disabled ? ' sign-button-disabled' : '';

  return `
    <style>
      .sign-button {
        margin-left: calc(var(--apryse-scale, 1) * -10px);
        border: calc(var(--apryse-scale, 1) * 2px) solid #0D8899;
        border-radius: calc(var(--apryse-scale, 1) * 4px);
        background-color: #FFF;
        cursor: pointer;
        font-family: 'halyard-text', Helvetica, Arial, sans-serif;
        box-shadow: 0 calc(var(--apryse-scale, 1) * 2px) calc(var(--apryse-scale, 1) * 10px) 0 rgba(19, 53, 98, 0.03), 0 calc(var(--apryse-scale, 1) * 15px) calc(var(--apryse-scale, 1) * 35px) 0 rgba(19, 53, 98, 0.15);
        width: calc(var(--apryse-scale, 1) * 400px);
        gap: calc(var(--apryse-scale, 1) * 16px);
        display: flex;
        padding: calc(var(--apryse-scale, 1) * 6px) calc(var(--apryse-scale, 1) * 16px);
      }

      .sign-button:hover {
        background-color: #F2FBFC;
      }

      .sign-button-disabled,
      .sign-button-disabled:hover {
        border-color: #CCCFD7;
        background-color: #F2F3F5;
        cursor: not-allowed;
        pointer-events: none;
        box-shadow: none;
        opacity: 0.7;
      }

      .sign-button-info {
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .sign-button-name {
        font-size: calc(var(--apryse-scale, 1) * 14px);
        font-weight: 400;
        line-height: calc(var(--apryse-scale, 1) * 20px);
        color: #000F37;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      .sign-button-email {
        font-size: calc(var(--apryse-scale, 1) * 12px);
        font-style: normal;
        font-weight: 300;
        line-height: calc(var(--apryse-scale, 1) * 16px);
        color: #4D5773;
      }

      .sign-button-action {
        font-size: calc(var(--apryse-scale, 1) * 14px);
        font-weight: 700;
        line-height: calc(var(--apryse-scale, 1) * 16px);
        color: #666F87;
        height: calc(var(--apryse-scale, 1) * 42px);
        display: flex;
        align-items: center;
      }
    </style>
    <div class="sign-button${disabledClass}">
      <span class="sign-button-action">${actionLabel}</span>
      ${contactInfoHTML}
    </div>
  `;
}

/**
 * Pre-PP-3513 red "Sign here" / "Initial here" button. Used when the multi-identity
 * feature is inactive so the original look is preserved for the legacy single-contact flow.
 *
 * Dimensions are scaled via the `--apryse-scale` CSS variable, which is set on the
 * sign-here wrapper element by the caller and updated live on zoom changes.
 */
export function getLegacySignHereButtonHTML(isSignature: boolean): string {
  // !important is required on background/color to defeat the
  // `button div div { background: transparent !important; color: inherit !important; }`
  // override in src/assets/apryse/stylesheet.css that strips Apryse's grey placeholder.
  return `
    <div style="
      background-color: #AE0404 !important;
      background: #AE0404 !important;
      color: white !important;
      padding: calc(var(--apryse-scale, 1) * 4px) calc(var(--apryse-scale, 1) * 8px) !important;
      font-size: calc(var(--apryse-scale, 1) * 12px) !important;
      font-weight: 500 !important;
      text-align: center !important;
      cursor: pointer !important;
      border-radius: calc(var(--apryse-scale, 1) * 2px) !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: auto !important;
      height: auto !important;
      box-sizing: border-box !important;
    ">${isSignature ? 'Sign here' : 'Initial here'}</div>
  `;
}

export function getInitialsButtonHTML({ actionLabel, initials, disabled = false }: InitialsButtonParams): string {
  const disabledClass = disabled ? ' initials-button-disabled' : '';

  return `
    <style>
      .initials-button {
        margin-left: calc(var(--apryse-scale, 1) * -7px);
        border: calc(var(--apryse-scale, 1) * 2px) solid #0D8899;
        border-radius: calc(var(--apryse-scale, 1) * 4px);
        background-color: #FFF;
        cursor: pointer;
        font-family: 'halyard-text', Helvetica, Arial, sans-serif;
        box-shadow: 0 calc(var(--apryse-scale, 1) * 2px) calc(var(--apryse-scale, 1) * 10px) 0 rgba(19, 53, 98, 0.03), 0 calc(var(--apryse-scale, 1) * 15px) calc(var(--apryse-scale, 1) * 35px) 0 rgba(19, 53, 98, 0.15);
        width: calc(var(--apryse-scale, 1) * 90px);
        padding: calc(var(--apryse-scale, 1) * 6px) calc(var(--apryse-scale, 1) * 8px);
      }

      .initials-button:hover {
        background-color: #F2FBFC;
      }

      .initials-button-disabled,
      .initials-button-disabled:hover {
        border-color: #CCCFD7;
        background-color: #F2F3F5;
        cursor: not-allowed;
        pointer-events: none;
        box-shadow: none;
        opacity: 0.7;
      }

      .initials-button-action {
        font-size: calc(var(--apryse-scale, 1) * 14px);
        font-weight: 700;
        line-height: calc(var(--apryse-scale, 1) * 16px);
        color: #666F87;
      }
    </style>
    <div class="initials-button${disabledClass}">
      <span class="initials-button-action">${actionLabel}</span>
      <br />
      <span>${escapeHtml(initials)}</span>
    </div>
  `;
}

export function getSignedLabelHTML(isSignature: boolean): string {
  const width = isSignature ? '400px' : '92px';

  return `
    <style>
      .signed-label {
        margin-left: -10px;
        width: ${width};
        padding: 4px 8px;
        font-family: 'halyard-text', Helvetica, Arial, sans-serif;
        font-size: 12px;
        font-weight: 700;
        line-height: 16px;
        color: #0D8899;
      }
    </style>
    <div class="signed-label">Signed</div>
  `;
}

/**
 * Normalize the page placement of Consent radio buttons (Yes/No) when a pair
 * visually straddles a page break but both widgets kept the OLD pageNumber.
 *
 * Why this exists:
 * - Apryse/WebViewer does not automatically reassign a form widget to the next page
 *   when its rect crosses a page boundary. In some templates, a Consent pair ends up
 *   with both radios on page N (one near the top, one near the bottom).
 *
 * What we do (per contactId + blockNumber):
 * - Find Consent radio pairs.
 * - If both are on the same page and their vertical gap > 50% of that page's height,
 *   assume they straddle a page break.
 * - Move the "upper" radio to the next page (preserve X and height; clamp Y).
 * - Redraw only the pages we touched.
 *
 * Idempotent: running multiple times is safe.
 */
export async function verifyOrUpdateConsentRadioButtonPositions(
  instance: WebViewerInstance,
  debug: boolean = false
): Promise<void> {
  const core = instance.Core;
  const document = core.documentViewer.getDocument();
  const annotationManager = core.annotationManager;

  const LOG_LABEL = '[Consent Radios] Split-across-page normalization';

  const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(max, value));

  // Only target Consent radio widgets using field-name helpers
  const isConsentRadioWidget = (widget: Annotation) =>
    widget instanceof core.Annotations.RadioButtonWidgetAnnotation &&
    getFieldType(widget.getField()?.name) === DocumentFieldType.CONSENT;

  // Group radios by "contactId#blockNumber" so we treat each pair together
  const groupKeyFromWidget = (widget: Annotation) => {
    const fieldName = widget instanceof core.Annotations.RadioButtonWidgetAnnotation ? widget.getField()?.name || '' : '';
    return `${getFieldDocumentContactId(fieldName)}#${getSignatureBlockNumber(fieldName)}`;
  };

  // Collect and filter
  const consentRadioWidgets = annotationManager.getAnnotationsList().filter(isConsentRadioWidget);
  if (consentRadioWidgets.length === 0) return;

  // Build groups: contactId#blockNumber -> RadioWidget[]
  const groupedByBlock = new Map<string, any[]>();
  for (const widget of consentRadioWidgets) {
    const key = groupKeyFromWidget(widget);
    groupedByBlock.set(key, [...(groupedByBlock.get(key) || []), widget]);
  }

  const pagesNeedingRedraw = new Set<number>();
  let didChangePositions = false;

  if (debug) console.groupCollapsed(LOG_LABEL);

  for (const [groupKey, widgetsInGroup] of groupedByBlock.entries()) {
    // Expect 2 radios per consent block; skip if malformed but don't crash
    if (widgetsInGroup.length < 2) {
      if (debug) console.warn('Group has < 2 radios; skipping:', groupKey, widgetsInGroup.map(w => w.getField()?.name));
      continue;
    }

    // Sort so index 0 is the upper (smaller y1), index 1 is the lower
    const [upperWidget, lowerWidget] = [...widgetsInGroup].sort(
      (a, b) => a.getRect().y1 - b.getRect().y1
    );

    const upperPageNumber = upperWidget.getPageNumber();
    const lowerPageNumber = lowerWidget.getPageNumber();
    const upperRect = upperWidget.getRect();
    const lowerRect = lowerWidget.getRect();

    // Already on different pages → treat as correct
    if (upperPageNumber !== lowerPageNumber) {
      if (debug) {
        console.groupCollapsed(`Group ${groupKey}`);
        console.log('Already on different pages; skipping.', widgetsInGroup.map(w => ({
          field: w.getField()?.name, page: w.getPageNumber(),
        })));
        console.groupEnd();
      }
      continue;
    }

    const pageInfo = document.getPageInfo(upperPageNumber);
    if (!pageInfo) continue;

    const pageHeight = pageInfo.height;
    const verticalGap = Math.abs(lowerRect.y1 - upperRect.y1);

    if (debug) {
      console.groupCollapsed(`Group ${groupKey}`);
      console.table([
        { who: 'upper', field: upperWidget.getField()?.name, page: upperPageNumber, y1: upperRect.y1, y2: upperRect.y2, pageHeight },
        { who: 'lower', field: lowerWidget.getField()?.name, page: lowerPageNumber, y1: lowerRect.y1, y2: lowerRect.y2, pageHeight },
      ]);
      console.log('verticalGap:', verticalGap, 'threshold (pageHeight / 2):', pageHeight / 2);
    }

    // Core rule: if both are on the same page but far apart vertically ⇒ assume they straddle a page break
    const looksSplitAcrossPages = pageHeight > 0 && verticalGap > pageHeight / 2;
    if (!looksSplitAcrossPages) {
      if (debug) { console.log('Not a split per rule; skipping.'); console.groupEnd(); }
      continue;
    }

    // Move the upper widget to the next page, preserving X and height, clamping Y within next page
    const targetPageNumber = upperPageNumber + 1;
    const nextPageInfo = document.getPageInfo(targetPageNumber);
    if (!nextPageInfo) {
      if (debug) { console.log('No next page exists; cannot move.'); console.groupEnd(); }
      continue; // Skip the rest of the loop iteration if next page doesn't exist
    }

    const widgetHeight = upperRect.getHeight();
    const newY1 = clamp(upperRect.y1, 0, Math.max(0, nextPageInfo.height - widgetHeight));
    const newY2 = newY1 + widgetHeight;

    if (debug) {
      console.log('MOVING upper radio → next page:', {
        field: upperWidget.getField()?.name,
        fromPage: upperPageNumber,
        toPage: targetPageNumber,
        newY1,
        newY2,
      });
    }

    upperWidget.setPageNumber(targetPageNumber);
    upperWidget.setRect(new core.Math.Rect(upperRect.x1, newY1, upperRect.x2, newY2));

    pagesNeedingRedraw.add(upperPageNumber);
    pagesNeedingRedraw.add(targetPageNumber);
    didChangePositions = true;

    if (debug) console.groupEnd();
  }

  // Redraw only the pages we touched (await if the SDK returns a Promise)
  if (didChangePositions) {
    for (const pageNumber of pagesNeedingRedraw) {
      const maybePromise = annotationManager.drawAnnotations({ pageNumber });
      if (maybePromise && typeof (maybePromise as any).then === 'function') {
        await (maybePromise as Promise<void>);
      }
    }
  }

  if (debug) console.groupEnd();
}
